import { useEffect, useRef } from 'react'
import type { ComponentProps, ReactNode } from 'react'
import { useReserveNavigationState } from '~/navigation'
import { sva } from '~/styled-system/css'
import { AfternoonTea } from './AfternoonTea'
import { Buffet } from './Buffet'
import { ChildUse } from './ChildUse'
import { Christmas } from './Christmas'
import { Clear } from './Clear'
import { FreeFlow } from './FreeFlow'
import { Price } from './Price'
import { SeatAttributes } from './SeatAttributes'
import { SmokingAllowed } from './SmokingAllowed'

export type Props = ComponentProps<typeof FreeFlow> &
  ComponentProps<typeof AfternoonTea> &
  ComponentProps<typeof Buffet> &
  ComponentProps<typeof SmokingAllowed> &
  ComponentProps<typeof SeatAttributes> &
  ComponentProps<typeof ChildUse> &
  ComponentProps<typeof Price> &
  ComponentProps<typeof Christmas> &
  ComponentProps<typeof Clear> & {
    useShow: () => boolean
  }

type FilterChip = { node: ReactNode; checked: boolean; enabled: boolean; defaultOrder: number }

const styles = sva({
  slots: ['container'],
  base: {
    container: {
      display: 'flex',
      gridGap: '1',
      overflowX: 'auto',
      whiteSpace: 'nowrap',
      paddingX: '3',
      paddingBottom: '2',
      _pc: {
        gridGap: '2',
        paddingX: '0',
        whiteSpace: 'normal',
        flexWrap: 'wrap',
      },
    },
  },
})

const classes = styles()

// FIXME px-3, pb-2 は利用箇所によって外部から与える
export function FilterChips(props: Props) {
  const filterChips: FilterChip[] = useSortByChecked(props)
  const show = props.useShow()
  const ref = useRef<HTMLDivElement>(null)
  const context = useReserveNavigationState('GUIDE', 'COURSE_SELECT')
  useEffect(() => {
    if (ref.current === null || context === undefined) {
      return
    }
    // 絞り込み条件が変わったらスクロール位置をリセットする
    ref.current.scrollTo({ left: 0, behavior: 'smooth' })
  }, [ref, context])
  if (!show) {
    return null
  }
  return (
    <div ref={ref} className={classes.container}>
      {filterChips.map((fc) => fc.node)}
    </div>
  )
}

function useSortByChecked({
  useFilterCriteria,
  useFreeFlows,
  useSetFreeFlows,
  useEnabledFreeFlows,
  useAfternoonTea,
  useSetAfternoonTea,
  useEnabledAfternoonTea,
  useBuffet,
  useSetBuffet,
  useEnabledBuffet,
  useSmokingAllowed,
  useSetSmokingAllowed,
  useEnabledSmokingAllowed,
  useSeatAttributes,
  useSetSeatAttributes,
  useEnabledSeatAttributes,
  useChildUse,
  useSetChildUse,
  useEnabledChildUse,
  useApplyPrice,
  useInitialPriceRange,
  useChristmas,
  useEnabledChristmas,
  useSetChristmas,
  useClear,
}: Props): FilterChip[] {
  const criteria = useFilterCriteria()
  const enabledFreeFlows = useEnabledFreeFlows()
  const enabledAfternoonTea = useEnabledAfternoonTea()
  const enabledBuffet = useEnabledBuffet()
  const enabledSmokingAllowed = useEnabledSmokingAllowed()
  const enabledSeatAttributes = useEnabledSeatAttributes()
  const enabledChildUse = useEnabledChildUse()
  const enabledChristmas = useEnabledChristmas()
  const chips: FilterChip[] = [
    {
      node: (
        <Price
          key="Price"
          useFilterCriteria={useFilterCriteria}
          useApplyPrice={useApplyPrice}
          useInitialPriceRange={useInitialPriceRange}
        />
      ),
      checked: criteria?.upperBoundPrice !== undefined || criteria?.lowerBoundPrice !== undefined,
      enabled: true,
      defaultOrder: 0,
    },

    {
      node: (
        <SeatAttributes
          key="SeatAttributes"
          useFilterCriteria={useFilterCriteria}
          useSeatAttributes={useSeatAttributes}
          useSetSeatAttributes={useSetSeatAttributes}
          useEnabledSeatAttributes={useEnabledSeatAttributes}
        />
      ),
      checked:
        (criteria?.seatTypes?.privateRoom ?? false) ||
        (criteria?.seatTypes?.semiPrivateRoom ?? false) ||
        (criteria?.seatTypes?.terrace ?? false) ||
        (criteria?.seatTypes?.counter ?? false) ||
        (criteria?.seatTypes?.table ?? false) ||
        (criteria?.seatTypes?.windowSide ?? false),
      enabled:
        enabledSeatAttributes.counter ||
        enabledSeatAttributes.privateRoom ||
        enabledSeatAttributes.semiPrivateRoom ||
        enabledSeatAttributes.terrace ||
        enabledSeatAttributes.table ||
        enabledSeatAttributes.windowSide,
      defaultOrder: 1,
    },
    {
      node: (
        <SmokingAllowed
          key="SmokingAllowed"
          useFilterCriteria={useFilterCriteria}
          useSmokingAllowed={useSmokingAllowed}
          useSetSmokingAllowed={useSetSmokingAllowed}
          useEnabledSmokingAllowed={useEnabledSmokingAllowed}
        />
      ),
      checked: criteria?.smokingAllowed === 'allow' || criteria?.smokingAllowed === 'notAllow',
      enabled: enabledSmokingAllowed.allow || enabledSmokingAllowed.notAllow,
      defaultOrder: 2,
    },
    {
      node: (
        <ChildUse
          key="ChildUse"
          useFilterCriteria={useFilterCriteria}
          useChildUse={useChildUse}
          useSetChildUse={useSetChildUse}
          useEnabledChildUse={useEnabledChildUse}
        />
      ),
      checked:
        (criteria?.childUses?.gradeSchoolers ?? false) ||
        (criteria?.childUses?.preSchoolers ?? false),
      enabled: enabledChildUse.gradeSchoolers || enabledChildUse.preSchoolers,
      defaultOrder: 3,
    },
    {
      node: (
        <FreeFlow
          key="FreeFlow"
          useFilterCriteria={useFilterCriteria}
          useFreeFlows={useFreeFlows}
          useSetFreeFlows={useSetFreeFlows}
          useEnabledFreeFlows={useEnabledFreeFlows}
        />
      ),
      checked: (criteria?.freeFlow ?? false) || (criteria?.freeFlowThreeHours ?? false),
      enabled: enabledFreeFlows.freeFlow || enabledFreeFlows.freeFlowThreeHours || false,
      defaultOrder: 4,
    },
    {
      node: (
        <Christmas
          key="Christmas"
          useFilterCriteria={useFilterCriteria}
          useChristmas={useChristmas}
          useEnabledChristmas={useEnabledChristmas}
          useSetChristmas={useSetChristmas}
        />
      ),
      checked: criteria?.christmas ?? false,
      enabled: enabledChristmas,
      defaultOrder: 5,
    },
    {
      node: (
        <AfternoonTea
          key="AfternoonTea"
          useFilterCriteria={useFilterCriteria}
          useAfternoonTea={useAfternoonTea}
          useSetAfternoonTea={useSetAfternoonTea}
          useEnabledAfternoonTea={useEnabledAfternoonTea}
        />
      ),
      checked: criteria?.afternoonTea ?? false,
      enabled: enabledAfternoonTea,
      defaultOrder: 6,
    },
    {
      node: (
        <Buffet
          key="Buffet"
          useFilterCriteria={useFilterCriteria}
          useBuffet={useBuffet}
          useSetBuffet={useSetBuffet}
          useEnabledBuffet={useEnabledBuffet}
        />
      ),
      checked: criteria?.buffet ?? false,
      enabled: enabledBuffet,
      defaultOrder: 7,
    },
    {
      node: <Clear key="Clear" useFilterCriteria={useFilterCriteria} useClear={useClear} />,
      checked: false,
      enabled: false,
      defaultOrder: 99,
    },
  ]

  return chips.sort(compare)
}

function compare(a: FilterChip, b: FilterChip) {
  if (a.enabled && !b.enabled) {
    return -1
  }
  if (!a.enabled && b.enabled) {
    return 1
  }
  if (a.checked && !b.checked) {
    return -1
  }
  if (!a.checked && b.checked) {
    return 1
  }
  return a.defaultOrder - b.defaultOrder
}
